import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { isMonetagConfigured, showRewardedAd } from '@/services/monetag';
import { useUiStore } from '@/store/uiStore';

/** Placeholder countdown length used when no ad network is wired yet. */
const COUNTDOWN_SECONDS = 5;

type Phase = 'ad' | 'counting' | 'ready';

/**
 * Ad interstitial before a signed-in user's final download. When a Monetag
 * rewarded zone is configured it plays a real ad and unlocks the download when
 * finished; otherwise it shows a short countdown placeholder. Either way the
 * download is a user-clicked "Download now" button so browsers never block it.
 */
export function AdGateModal() {
  const open = useUiStore((s) => s.adGateOpen);
  const gate = useUiStore((s) => s.adGate);
  const close = useUiStore((s) => s.closeAdGate);
  const [phase, setPhase] = useState<Phase>('counting');
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!open) return;

    // Real rewarded ad (Monetag) — unlock when the user finishes it. Any
    // failure (ad blocker, no fill) still unlocks so users aren't trapped.
    if (isMonetagConfigured()) {
      setPhase('ad');
      let cancelled = false;
      showRewardedAd()
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setPhase('ready');
        });
      return () => {
        cancelled = true;
      };
    }

    // No ad network yet → placeholder countdown gate.
    setPhase('counting');
    setSeconds(COUNTDOWN_SECONDS);
    const id = window.setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          window.clearInterval(id);
          setPhase('ready');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [open]);

  const ready = phase === 'ready';

  return (
    <Modal open={open} onClose={close} title="Your download is almost ready">
      {gate && (
        <div className="space-y-4">
          <p className="line-clamp-2 text-sm text-zinc-400">{gate.title}</p>

          {/* Ad surface (Monetag renders its own fullscreen ad on top of this). */}
          <div className="flex h-52 flex-col items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] text-center sm:h-60">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-600">
              Advertisement
            </span>
            <span className="px-6 text-xs text-zinc-500">
              {phase === 'ad'
                ? 'Loading your ad…'
                : 'Thanks for supporting free downloads.'}
            </span>
          </div>

          {ready ? (
            <a
              href={gate.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              onClick={close}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary-500 to-accent-500 text-base font-semibold text-white shadow-glow transition-all hover:brightness-110 active:scale-[0.98] focus-ring"
            >
              <Download className="h-5 w-5" aria-hidden />
              Download now
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="flex h-12 w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-white/5 text-sm font-semibold text-zinc-500"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {phase === 'ad' ? 'Loading ad…' : `Download unlocks in ${seconds}s…`}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
}
